/**
 * Lightweight YAML frontmatter parser/serializer for SKILL.md and AGENT.md files.
 * Handles the --- delimited frontmatter block at the top of markdown files.
 */

export interface ParsedFrontmatter {
  attributes: Record<string, unknown>;
  body: string;
}

export function parseFrontmatter(raw: string): ParsedFrontmatter {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) {
    return { attributes: {}, body: raw };
  }

  const yamlBlock = match[1];
  const body = match[2];
  const attributes: Record<string, unknown> = {};

  for (const line of yamlBlock.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) continue;

    const key = trimmed.slice(0, colonIdx).trim();
    let value: unknown = trimmed.slice(colonIdx + 1).trim();

    // Parse common YAML value types
    if (value === 'true') value = true;
    else if (value === 'false') value = false;
    else if (value === '' || value === 'null') value = null;
    else if (/^\d+$/.test(value as string)) value = parseInt(value as string, 10);

    attributes[key] = value;
  }

  return { attributes, body };
}

export function serializeFrontmatter(attributes: Record<string, unknown>, body: string): string {
  const lines: string[] = [];

  for (const [key, value] of Object.entries(attributes)) {
    if (value === null || value === undefined || value === '') continue;
    lines.push(`${key}: ${value}`);
  }

  const frontmatter = lines.length > 0 ? `---\n${lines.join('\n')}\n---\n` : '';
  return frontmatter + body;
}
